import React, { useState, useEffect } from 'react';
import { useUserManagement } from '../hooks/useUserManagement';
import { 
  HiArrowPath, 
  HiMagnifyingGlass, 
  HiExclamationCircle,
  HiUsers,
  HiBell,
  HiEye,
  HiDevicePhoneMobile,
  HiSquares2X2,
  HiArrowDownTray,
  HiCheckCircle,
  HiPencilSquare
} from 'react-icons/hi2';
import SendNotificationDialog from './SendNotificationDialog';
import WearableDetailsDialog from './WearableDetailsDialog';
import UpdateSubscriptionDialog from './UpdateSubscriptionDialog';
import { useNavigate } from 'react-router-dom';
import { TerraDevice } from '../types/user';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase/config';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const UserManagement: React.FC = () => {
  const { users, loading, error, refreshData } = useUserManagement();
  const [localUsers, setLocalUsers] = useState(users);
  const [searchTerm, setSearchTerm] = useState('');
  const [notificationDialog, setNotificationDialog] = useState<{
    isOpen: boolean;
    targetUser: {
      id: string;
      name: string;
      surname: string;
      email: string;
    } | null;
  }>({
    isOpen: false,
    targetUser: null
  });

  const [wearableDialog, setWearableDialog] = useState<{
    isOpen: boolean;
    targetUser: {
      id: string;
      name: string;
      surname: string;
      email: string;
    } | null;
    devices: TerraDevice[];
  }>({
    isOpen: false,
    targetUser: null,
    devices: []
  });

  const [checkingSubscriptions, setCheckingSubscriptions] = useState(false);

  const [subscriptionDialog, setSubscriptionDialog] = useState<{
    isOpen: boolean;
    targetUser: {
      id: string;
      name: string;
      surname: string;
      email: string;
    } | null;
    currentSubscription: string;
  }>({
    isOpen: false,
    targetUser: null,
    currentSubscription: 'default'
  });

  const { currentUser } = useAuth();
  const navigate = useNavigate(); 

  // Sync localUsers with users from the hook
  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  const normalizeText = (value: unknown) =>
    typeof value === 'string' ? value.toLowerCase() : '';
  const normalizedSearchTerm = normalizeText(searchTerm);

  // Filter users based on search term
  const filteredUsers = localUsers.filter((user) =>
    normalizeText(user.userData?.name).includes(normalizedSearchTerm) ||
    normalizeText(user.userData?.surname).includes(normalizedSearchTerm) ||
    normalizeText(user.userData?.email).includes(normalizedSearchTerm) ||
    normalizeText(user.id).includes(normalizedSearchTerm) ||
    normalizeText(user.activeSubscription?.subscriptionType).includes(normalizedSearchTerm) ||
    normalizeText(user.activeSubscription?.subscriptionVia).includes(normalizedSearchTerm)
  );

  const subscriptionOptions = [
    { value: 'default', label: 'Default', color: 'badge-neutral' },
    { value: 'premium', label: 'Premium', color: 'badge-success' },
    { value: 'platinum', label: 'Platinum', color: 'badge-warning' },
    { value: 'plus', label: 'Plus', color: 'badge-success' },
    { value: 'coach', label: 'Coach', color: 'badge-info' }
  ];

  const handleNotificationClick = (
  user: {
    id: string;
    userData?: {
      name?: string;
      surname?: string;
      email?: string;
    };
  }
) => {
  const userData = user.userData ?? {};
  setNotificationDialog({
    isOpen: true,
    targetUser: {
      id: user.id,
      name: userData.name ?? "Unknown",
      surname: userData.surname ?? "",
      email: userData.email ?? "",
    },
  });
};

const handleWearableClick = (
  user: {
    id: string;
    userData?: {
      name?: string;
      surname?: string;
      email?: string;
      deviceIDs?: TerraDevice[];
    };
  }
) => {
  const userData = user.userData ?? {};
  setWearableDialog({
    isOpen: true,
    targetUser: {
      id: user.id,
      name: userData.name ?? "Unknown",
      surname: userData.surname ?? "",
      email: userData.email ?? "",
    },
    devices: userData.deviceIDs ?? [],
  });
};

  const handleNotificationDialogClose = () => {
    setNotificationDialog({
      isOpen: false,
      targetUser: null
    });
  };

  const handleWearableDialogClose = () => {
    setWearableDialog({
      isOpen: false,
      targetUser: null,
      devices: []
    });
  };

  const handleSubscriptionClick = (user: { id: string; userData?: { name?: string; surname?: string; email?: string }; activeSubscription?: { subscriptionType: string } | null }) => {
    const userData = user.userData ?? {};
    setSubscriptionDialog({
      isOpen: true,
      targetUser: {
        id: user.id,
        name: userData.name ?? "Unknown",
        surname: userData.surname ?? "",
        email: userData.email ?? ""
      },
      currentSubscription: user.activeSubscription?.subscriptionType || 'default'
    });
  };

  const handleSubscriptionDialogClose = () => {
    setSubscriptionDialog({
      isOpen: false,
      targetUser: null,
      currentSubscription: 'default'
    });
  };

  const handleUpdateSubscription = async (userId: string, subscriptionType: any) => {
    if (!currentUser) {
      throw new Error('You must be authenticated to update subscriptions');
    }

    try {
      // 1. Write to adminSubscriptions collection
      const adminSubscriptionRef = doc(db, 'adminSubscriptions', userId);
      await setDoc(adminSubscriptionRef, {
        userId,
        subscriptionType:subscriptionType.value,
        priceId:subscriptionType.priceId,
        lastUpdated: serverTimestamp(),
        subscriptionVia:"admin",
        adminUserID: currentUser.uid
      });

      console.log(`[UPDATE_SUBSCRIPTION] Admin subscription updated for user ${userId}`);

      // 2. Trigger subscription check via Firebase Function
      const functions = getFunctions();
      const triggerCheckUserSubscription = httpsCallable(functions, 'triggerCheckUserSubscription');
      
      const result = await triggerCheckUserSubscription({ userId });
      console.log('[UPDATE_SUBSCRIPTION] Subscription check result:', result.data);

      // 3. Fetch only the updated user's active subscription
      const activeSubscriptionRef = doc(db, 'activeSubscriptions', userId);
      const activeSubscriptionDoc = await getDoc(activeSubscriptionRef);

      if (activeSubscriptionDoc.exists()) {
        const data = activeSubscriptionDoc.data();
        
        // Safely convert Firestore timestamp to date
        let lastUpdatedDate: Date;
        try {
          lastUpdatedDate = data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date(data.lastUpdated || Date.now());
        } catch (error) {
          console.warn('Error converting lastUpdated date:', error, data.lastUpdated);
          lastUpdatedDate = new Date(); // Fallback to current date
        }

        const updatedSubscription = {
          userId: activeSubscriptionDoc.id,
          subscriptionType: data.subscriptionType || 'default',
          subscriptionVia: data.subscriptionVia || 'default',
          lastUpdated: lastUpdatedDate,
          subscriptionChangeMessage: data.subscriptionChangeMessage || '',
          subscriptionChangeRead: data.subscriptionChangeRead ?? true,
        };

        // 4. Update only this user in the local state
        setLocalUsers(prevUsers => 
          prevUsers.map(user => 
            user.id === userId 
              ? { ...user, activeSubscription: updatedSubscription }
              : user
          )
        );

        console.log(`[UPDATE_SUBSCRIPTION] Updated user ${userId} in local state`);
      }

      alert(`Successfully updated subscription for user to ${subscriptionType.label}`);
    } catch (error) {
      console.error('[UPDATE_SUBSCRIPTION] Error updating subscription:', error);
      throw error;
    }
  };


  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    
    // Check if date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.warn('Date formatting error:', error, date);
      return 'Invalid Date';
    }
  };

  const downloadCSV = () => {
    // Prepare CSV headers
    const headers = ['Name', 'Surname', 'Email', 'UID', 'Role', 'Subscription Type', 'Subscription Source', 'Last Updated'];
    
    // Remove duplicates by email (keep the newest one)
    const uniqueUsers = filteredUsers.reduce((acc, user) => {
      const email = user.userData?.email || '';
      const existingUser = acc.find(u => (u.userData?.email || '') === email);
      
      if (!existingUser) {
        acc.push(user);
      } else {
        // Keep the newer user (with more recent last updated date)
        const existingDate = existingUser.activeSubscription?.lastUpdated?.getTime() || 0;
        const currentDate = user.activeSubscription?.lastUpdated?.getTime() || 0;
        
        if (currentDate > existingDate) {
          const index = acc.indexOf(existingUser);
          acc[index] = user;
        }
      }
      
      return acc;
    }, [] as typeof filteredUsers);
    
    // Sort by last updated date (newest first)
    const sortedUsers = [...uniqueUsers].sort((a, b) => {
      const dateA = a.activeSubscription?.lastUpdated?.getTime() || 0;
      const dateB = b.activeSubscription?.lastUpdated?.getTime() || 0;
      return dateB - dateA; // Descending order (newest first)
    });
    
    // Prepare CSV rows
    const rows = sortedUsers.map(user => {
      return [
        user.userData?.name || '',
        user.userData?.surname || '',
        user.userData?.email || '',
        user.id || '',
        user.userData?.role || '',
        user.activeSubscription?.subscriptionType || 'default',
        user.activeSubscription?.subscriptionVia || 'default',
        formatDate(user.activeSubscription?.lastUpdated || null)
      ];
    });

    // Escape CSV fields (handle commas, quotes, and newlines)
    const escapeCSVField = (field: string) => {
      // Handle undefined/null values
      const stringField = field ?? '';
      
      if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
        return `"${stringField.replace(/"/g, '""')}"`;
      }
      return stringField;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSVField).join(','),
      ...rows.map(row => row.map(escapeCSVField).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `user-management-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCheckAllSubscriptions = async () => {
    // Confirm action
    if (!window.confirm(
      `This will check and update active subscriptions for all ${localUsers.length} users based on their trial and admin subscriptions. Continue?`
    )) {
      return;
    }

    setCheckingSubscriptions(true);

    try {
      const functions = getFunctions();
      const checkAllSubscriptions = httpsCallable(functions, 'triggerCheckAllUsersSubscription');
      
      const result = await checkAllSubscriptions();
      const data = result.data as { 
        success: boolean; 
        totalUsers: number; 
        processedCount: number; 
        errorCount: number; 
        message: string;
      };

      if (data.success) {
        alert(`Success! ${data.message}`);
        // Refresh the user list to show updated subscriptions
        await refreshData();
      } else {
        alert('Check completed with errors. See console for details.');
      }
    } catch (error) {
      console.error('Subscription check failed:', error);
      alert(`Subscription check failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setCheckingSubscriptions(false);
    }
  };

  if (loading && localUsers.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg mb-4"></div>
          <p className="text-base-content/80">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <h1 className="text-3xl font-semibold">User Management</h1>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleCheckAllSubscriptions}
            disabled={checkingSubscriptions || localUsers.length === 0}
            className="btn btn-info btn-sm text-white"
            title="Check and update all users' active subscriptions"
          >
            {checkingSubscriptions ? (
              <>
                <span className="loading loading-spinner loading-sm mr-2"></span>
                Checking...
              </>
            ) : (
              <>
                <HiCheckCircle className="w-4 h-4 mr-2" />
                Check All Subscriptions
              </>
            )}
          </button>
          <button
            onClick={downloadCSV}
            disabled={filteredUsers.length === 0}
            className="btn btn-success btn-sm text-white"
            title={`Download ${filteredUsers.length} user(s) as CSV`}
          >
            <HiArrowDownTray className="w-4 h-4 mr-2" />
            Download CSV
          </button>
          <button
            onClick={refreshData}
            disabled={loading}
            className="btn btn-primary btn-sm"
          >
            <HiArrowPath className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error mb-6">
          <HiExclamationCircle className="w-5 h-5" />
          <span>{error.message}</span>
        </div>
      )}

      {/* Search and Stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="relative">
          <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users by name, email, UID, or subscription..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input input-bordered pl-10 w-full md:w-96"
          />
        </div>
        
        <div className="flex items-center space-x-4 text-sm text-base-content/80">
          <div className="flex items-center space-x-1">
            <HiUsers className="w-4 h-4" />
            <span>Total: {localUsers.length}</span>
          </div>
          <div className="flex items-center space-x-1">
            <span>Showing: {filteredUsers.length}</span>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-warning">
              {localUsers.filter(u => u.activeSubscription?.subscriptionType === 'platinum').length}
            </div>
            <div className="text-sm text-base-content/80">Platinum Users</div>
          </div>
        </div>
        
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-success">
              {localUsers.filter(u => u.activeSubscription?.subscriptionType === 'premium').length}
            </div>
            <div className="text-sm text-base-content/80">Premium Users</div>
          </div>
        </div>
        
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-info">
              {localUsers.filter(u => u.activeSubscription?.subscriptionType === 'coach').length}
            </div>
            <div className="text-sm text-base-content/80">Coach Users</div>
          </div>
        </div>
        
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-neutral">
              {localUsers.filter(u => u.activeSubscription?.subscriptionType === 'default' || !u.activeSubscription).length}
            </div>
            <div className="text-sm text-base-content/80">Default Users</div>
          </div>
        </div>
        
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body p-4">
            <div className="text-2xl font-bold text-secondary">
              {localUsers.filter(u => u.activeSubscription?.subscriptionType === 'plus').length}
            </div>
            <div className="text-sm text-base-content/80">Plus Users</div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card bg-base-100 shadow-sm border border-base-200">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table table-zebra w-full">
              <thead>
                <tr className="bg-base-100">
                  <th className="font-semibold">User</th>
                  <th className="font-semibold">UID</th>
                  <th className="font-semibold">Email</th>
                  <th className="font-semibold">Active Subscription</th>
                  <th className="font-semibold">Last Updated</th>
                  <th className="font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-base-content/60">
                      {searchTerm ? 'No users found matching your search.' : 'No users found.'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => {
                    const displayName = user.userData?.name ?? '';
                    const displaySurname = user.userData?.surname ?? '';
                    const displayEmail = user.userData?.email ?? 'N/A';
                    const displayRole = user.userData?.role ?? 'N/A';
                    const displayImageUrl = user.userData?.imageUrlTiny ?? user.userData?.imageUrl ?? '';
                    const displayDeviceIds = user.userData?.deviceIDs ?? [];
                    const avatarInitial = displayName.charAt(0).toUpperCase() || '?';

                    return (
                    <tr key={user.id} className="hover:bg-base-200">
                      <td>
                        <div className="flex items-center space-x-3">
                          {displayImageUrl ? (
                            <img 
                              src={displayImageUrl} 
                              alt={`${displayName || 'User'}'s avatar`}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                              {avatarInitial}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">
                              {displayName} {displaySurname}
                            </div>
                            <div className="text-sm text-base-content/60">
                              {displayRole}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <code className="text-xs bg-base-200 px-2 py-1 rounded">
                          {user.id}
                        </code>
                      </td>
                      <td>{displayEmail}</td>
                      <td>
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className={`badge ${
                              user.activeSubscription?.subscriptionType === 'platinum' ? 'badge-warning' :
                              user.activeSubscription?.subscriptionType === 'premium' ? 'badge-success' :
                              user.activeSubscription?.subscriptionType === 'coach' ? 'badge-info' :
                              user.activeSubscription?.subscriptionType === 'plus' ? 'badge-secondary' :
                              'badge-neutral'
                            } badge-sm`}>
                              {user.activeSubscription?.subscriptionType || 'default'}
                            </span>
                          </div>
                          <span className="text-xs text-base-content/60">
                            via {user.activeSubscription?.subscriptionVia || 'default'}
                          </span>
                        </div>
                      </td>
                      <td className="text-sm">
                        {formatDate(user.activeSubscription?.lastUpdated || null)}
                      </td>
                      <td>
                        <div className="flex flex-col space-y-1">
                          <button
                            onClick={() => handleSubscriptionClick(user)}
                            className="btn btn-sm btn-ghost text-secondary hover:bg-secondary hover:text-white"
                            title={`Update admin subscription for ${displayName || 'user'}`}
                          >
                            <HiPencilSquare className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleNotificationClick(user)}
                            className="btn btn-sm btn-ghost text-primary hover:bg-primary hover:text-white"
                            title={`Send notification to ${displayName || 'user'}`}
                          >
                            <HiBell className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(`/feed/?uid=${user.id}`, '_blank')}
                            className="btn btn-sm btn-ghost text-primary hover:bg-primary hover:text-white"
                            title={`View ${(displayName || 'user')}'s feed`}
                          >
                            <HiEye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => window.open(`/dashboard/?uid=${user.id}`, '_blank')}
                            className="btn btn-sm btn-ghost text-info hover:bg-info hover:text-white"
                            title={`View ${(displayName || 'user')}'s dashboard`}
                          >
                            <HiSquares2X2 className="w-4 h-4" />
                          </button>
                          {displayDeviceIds.length > 0 && (
                            <button
                              onClick={() => handleWearableClick(user)}
                              className="btn btn-sm btn-ghost text-info hover:bg-info hover:text-white"
                              title={`View ${(displayName || 'user')}'s wearable devices (${displayDeviceIds.length})`}
                            >
                              <HiDevicePhoneMobile className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Notification Dialog */}
      {notificationDialog.targetUser && (
        <SendNotificationDialog
          isOpen={notificationDialog.isOpen}
          onClose={handleNotificationDialogClose}
          targetUser={notificationDialog.targetUser}
        />
      )}

      {/* Wearable Details Dialog */}
      {wearableDialog.targetUser && (
        <WearableDetailsDialog
          isOpen={wearableDialog.isOpen}
          onClose={handleWearableDialogClose}
          user={wearableDialog.targetUser}
        />
      )}

      {/* Update Subscription Dialog */}
      {subscriptionDialog.targetUser && (
        <UpdateSubscriptionDialog
          isOpen={subscriptionDialog.isOpen}
          onClose={handleSubscriptionDialogClose}
          targetUser={subscriptionDialog.targetUser}
          currentSubscription={subscriptionDialog.currentSubscription}
          onUpdate={handleUpdateSubscription}
        />
      )}
    </div>
  );
};

export default UserManagement; 